import React, { ReactNode, useEffect, useState } from 'react';
import ColorThief from "colorthief";
import { BenefitAllocation } from '@/types/benefit-allocation/benefit-allocation';
import BenefitAction from '../benefit-action/benefit-action';
import {
    DetailContainer,
    ImageContainerStyled,
    ImageStyled,
    DividerStyled,
    EllipseLeft,
    EllipseRight,
    DetailStyled,
    Title,
    Description,
    DashContainerStyled,
    DashStyled,
    ContentWrapper,
    ActionWrapper,
    ValidUntil,
    RedeemedMessage
} from './style-component';
import { Skeleton } from 'antd';
import { BenefitActionProps } from '../benefit-action/benefit-action-types';

interface BenefitDetailsProps {
    benefit: BenefitAllocation;
    children?: ReactNode;
    loading?: boolean;
}

const BenefitDetails: React.FC<BenefitDetailsProps> = ({ benefit, children, loading }) => {
    const [dominantColor, setDominantColor] = useState<string>("rgba(23, 36, 43)");
    const [redeemed, setRedeemed] = useState<boolean>(false);

    useEffect(() => {

        const benefitsAvailable = benefit.owned_amount >= 1;
        //Set redeemed state to false if there are no owned benefits available
        setRedeemed(!benefitsAvailable);

        if (benefit?.benefit?.reward?.thumb_url) {
            const img = new Image();
            img.crossOrigin = "Anonymous";
            img.src = benefit?.benefit?.reward?.thumb_url;

            img.onload = () => {
                try {
                    const colorThief = new ColorThief();
                    const dominantRGB = colorThief.getColor(img);
                    setDominantColor(`rgba(${dominantRGB[0]}, ${dominantRGB[1]}, ${dominantRGB[2]}, 0.5)`);
                } catch (error) {
                    console.error("Error extracting color from image:", error);
                }
            };
        }
    }, [benefit]);

    return (
        <DetailContainer>
            <ContentWrapper>
                <ImageContainerStyled $backgroundColor={dominantColor}>
                    {loading ?
                        <Skeleton.Image active={loading} />
                        :
                        <ImageStyled src={benefit?.benefit?.reward?.thumb_url} alt={benefit?.benefit?.reward?.name} />
                    }
                </ImageContainerStyled>
                <DetailStyled $backgroundColor={dominantColor}>
                    <Title>{benefit?.benefit?.reward?.name}</Title>
                    <Description>{benefit?.benefit?.reward?.description}</Description>
                    <ValidUntil>
                        <span>Válido hasta:</span>
                        <span>31 de Marzo del 2025</span>
                    </ValidUntil>
                </DetailStyled>
            </ContentWrapper>
            <DividerStyled $backgroundColor={dominantColor}>
                <EllipseLeft color={dominantColor} $redeemed={redeemed} />
                <DashContainerStyled $backgroundColor={dominantColor} $redeemed={redeemed}>
                    <DashStyled $backgroundColor={dominantColor} />
                </DashContainerStyled>
                <EllipseRight color={dominantColor} $redeemed={redeemed} />
            </DividerStyled>
            <ActionWrapper $backgroundColor={dominantColor} $redeemed={redeemed}>
                {(!loading && redeemed) ?
                    <RedeemedMessage data-testid="redeemed-message">
                        <h3>¡Ya usaste este beneficio!</h3>
                        <p>Completa nuevas misiones y sigue acumulando más beneficios como este.</p>
                    </RedeemedMessage>
                    :
                    <BenefitAction
                        data-testid="benefit-details"
                        benefit={benefit}
                        type={benefit?.benefit?.details?.instructions?.type as BenefitActionProps['type']}
                    />
                }
                {children}
            </ActionWrapper>
        </DetailContainer>
    );
};

export default BenefitDetails;